import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ControlledInput } from 'src/components/ui/Input/Input';
import { ControlledSelect } from 'src/components/ui/Select/Select';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { toast } from 'react-toastify';
import { $publicApi } from 'src/services/api';
import logger from 'src/utils/logger';
import { CompanyDataFormValues, companyDataSchema, WarehouseCode } from '../schema';
import { extractBackendErrorMessage } from '../errorUtils';
import { useRegistrationFormContext } from '../RegistrationFormContext';
import { REGISTRATION_COUNTRY_OPTIONS } from '../countries';
import RegistrationFormErrors from '../RegistrationFormErrors';

interface CompanyDataStepProps {
  registrationKey: string;
  onAdvance: () => void;
}

const CompanyDataStep: React.FC<CompanyDataStepProps> = ({ registrationKey, onAdvance }) => {
  const { t } = useTranslation();
  const { formState, setFormState } = useRegistrationFormContext();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyDataFormValues>({
    resolver: zodResolver(companyDataSchema),
    defaultValues: formState.companyData,
    mode: 'onSubmit',
  });

  const { mutateAsync, isPending } = $publicApi.useMutation('put', '/registrations/{registration_key}');

  const onSubmit = async (values: CompanyDataFormValues) => {
    try {
      await mutateAsync({
        params: { path: { registration_key: registrationKey } },
        body: {
          company_name: values.company_name,
          company_tax_id: values.company_tax_id,
          company_bin: values.company_bin,
          warehouse: values.warehouse as WarehouseCode,
          address: values.address,
          remission_address: values.remission_address,
        },
      });
      setFormState(prev => ({ ...prev, companyData: values }));
      onAdvance();
    } catch (err) {
      logger.error('Failed to save company data', err);
      const backendMessage = extractBackendErrorMessage(err);
      toast.error(backendMessage || t('registration.errors.save_failed'));
    }
  };

  const warehouseOptions = [
    { value: 'WSAT', label: t('registration.warehouse.WSAT') },
    { value: 'WSDE', label: t('registration.warehouse.WSDE') },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-24">
      <h3 className="typo-title-24 text-full">{t('registration.steps.company_data')}</h3>

      <RegistrationFormErrors errors={errors} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
        <ControlledInput
          control={control}
          name="company_name"
          label={t('registration.fields.company_name')}
          placeholder=""
          className="col-span-1 md:col-span-2"
        />
        <ControlledInput
          control={control}
          name="company_tax_id"
          label={t('registration.fields.company_tax_id')}
          placeholder={t('registration.placeholders.company_tax_id')}
        />
        <ControlledInput
          control={control}
          name="company_bin"
          label={t('registration.fields.company_bin')}
          placeholder={t('registration.placeholders.company_bin')}
        />
        <ControlledSelect
          name="warehouse"
          control={control}
          options={warehouseOptions}
          label={t('registration.fields.warehouse')}
          placeholder={t('general.select')}
          containerClassName="col-span-1 md:col-span-2"
        />
      </div>

      <div>
        <h4 className="typo-title-20 text-full mb-12">{t('registration.fields.address')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <ControlledInput
            control={control}
            name="address.street"
            label={t('registration.fields.street')}
            placeholder=""
          />
          <ControlledInput
            control={control}
            name="address.street2"
            label={t('registration.fields.street2')}
            placeholder=""
          />
          <ControlledInput
            control={control}
            name="address.zip_code"
            label={t('registration.fields.zip_code')}
            placeholder={t('registration.placeholders.zip_code')}
          />
          <ControlledInput control={control} name="address.city" label={t('registration.fields.city')} placeholder="" />
          <ControlledSelect
            name="address.country_code"
            control={control}
            options={REGISTRATION_COUNTRY_OPTIONS}
            placeholder={t('registration.fields.country')}
            isSearchable
            containerClassName="col-span-1 md:col-span-2"
          />
        </div>
      </div>

      <div>
        <h4 className="typo-title-20 text-full mb-12">{t('registration.fields.remission_address')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <ControlledInput
            control={control}
            name="remission_address.company"
            label={t('registration.fields.remission_company')}
            placeholder=""
            className="col-span-1 md:col-span-2"
          />
          <ControlledInput
            control={control}
            name="remission_address.address.street"
            label={t('registration.fields.street')}
            placeholder=""
          />
          <ControlledInput
            control={control}
            name="remission_address.address.street2"
            label={t('registration.fields.street2')}
            placeholder=""
          />
          <ControlledInput
            control={control}
            name="remission_address.address.zip_code"
            label={t('registration.fields.zip_code')}
            placeholder={t('registration.placeholders.zip_code')}
          />
          <ControlledInput
            control={control}
            name="remission_address.address.city"
            label={t('registration.fields.city')}
            placeholder=""
          />
          <ControlledSelect
            name="remission_address.address.country_code"
            control={control}
            options={REGISTRATION_COUNTRY_OPTIONS}
            placeholder={t('registration.fields.country')}
            isSearchable
            containerClassName="col-span-1 md:col-span-2"
          />
        </div>
      </div>

      <div className="flex justify-end pt-16">
        <Button title={t('registration.next')} tone={ButtonToneProps.PRIMARY_BLUE} type="submit" disabled={isPending} />
      </div>
    </form>
  );
};

export default CompanyDataStep;
